import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface Option {
	code: string;
	name: string;
	[key: string]: any;
}

interface MultiSelectDropdownProps {
	options: Option[];
	placeholder?: string;
	value?: string[];
	onChange?: (selected: string[]) => void;
	onSearchChange?: (value: string) => void;
	single?: boolean;
	isSourceModal?: boolean;
	disabled?: boolean;
	previewKey?: keyof Option;
	subKey?: keyof Option;
	searchKeys?: string[];
	stayOpenOnUpdate?: boolean;
	style?: React.CSSProperties;
	dropdownStyle?: React.CSSProperties;
	buttonStyle?: React.CSSProperties;
	btnClassName?: string;
	showSelectAll?: boolean;
	allSelectedText?: string;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
	options,
	placeholder = 'Pilih filter...',
	value,
	onChange,
	onSearchChange,
	stayOpenOnUpdate,
	single = false,
	isSourceModal = false,
	disabled = false,
	previewKey = 'name',
	subKey,
	searchKeys,
	style,
	dropdownStyle,
	buttonStyle,
	btnClassName,
	showSelectAll = true,
	allSelectedText,
}) => {
	const [searchTerm, setSearchTerm] = useState('');
	const [open, setOpen] = useState(false);
	const prevOptionsRef = useRef<Option[]>([]);
	const [dropUp, setDropUp] = useState(false);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const [internalSelected, setInternalSelected] = useState<string[]>([]);

	const [coords, setCoords] = useState<{
		top?: number;
		bottom?: number;
		left: number;
		width: number;
		maxHeight: number;
		dropUp: boolean;
	}>({
		left: 0,
		width: 0,
		maxHeight: 300,
		dropUp: false,
	});

	const updatePosition = useCallback(() => {
		if (!buttonRef.current) return;
		const rect = buttonRef.current.getBoundingClientRect();
		const spaceBelow = window.innerHeight - rect.bottom;
		const spaceAbove = rect.top;
		const desiredHeight = dropdownStyle?.maxHeight
			? parseInt(String(dropdownStyle.maxHeight)) || 300
			: 300;

		const shouldDropUp = spaceBelow < 220 && spaceAbove > spaceBelow;
		setDropUp(shouldDropUp);

		const availableHeight = Math.max(
			100,
			shouldDropUp ? Math.min(spaceAbove - 12, desiredHeight) : Math.min(spaceBelow - 12, desiredHeight)
		);

		let left = rect.left;
		let width = Math.max(rect.width, 200);
		if (left + width > window.innerWidth - 10) {
			left = Math.max(10, window.innerWidth - width - 10);
		}
		if (left < 10) left = 10;

		setCoords({
			top: shouldDropUp ? undefined : rect.bottom + 4,
			bottom: shouldDropUp ? window.innerHeight - rect.top + 4 : undefined,
			left,
			width,
			maxHeight: availableHeight,
			dropUp: shouldDropUp,
		});
	}, [dropdownStyle?.maxHeight]);

	useLayoutEffect(() => {
		if (open) {
			updatePosition();
		}
	}, [open, updatePosition]);

	const selected = value !== undefined ? value : internalSelected;

	const updateSelected = (newSelected: string[]) => {
		if (value === undefined) setInternalSelected(newSelected);
		if (onChange) onChange(newSelected);
	};

	const toggleOption = (opt: string) => {
		let newSelected: string[];
		if (single) {
			newSelected = [opt];
			setOpen(false);
		} else {
			newSelected = selected.includes(opt)
				? selected.filter(item => item !== opt)
				: [...selected, opt];
		}
		updateSelected(newSelected);
	};

	const clearAll = () => {
		updateSelected([]);
		setSearchTerm('');
	};

	const filtered = options.filter(opt => {
		const term = searchTerm.toLowerCase();
		if (searchKeys && searchKeys.length > 0) {
			const match = searchKeys.some(key => opt[key] && String(opt[key]).toLowerCase().includes(term));
			return match;
		}
		return (
			String(opt[previewKey]).toLowerCase().includes(term) ||
			(subKey && opt[subKey] && String(opt[subKey]).toLowerCase().includes(term))
		);
	});

	const isAllSelected = options.length > 0 && options.every(item => selected.includes(item.code));
	const isAllFilteredSelected = filtered.length > 0 && filtered.every(item => selected.includes(item.code));

	const selectAll = () => {
		if (searchTerm.trim() !== '') {
			const filteredCodes = filtered.map(opt => opt.code);
			const newSelected = Array.from(new Set([...selected, ...filteredCodes]));
			updateSelected(newSelected);
		} else {
			updateSelected(options.map(opt => opt.code));
		}
	};

	useEffect(() => {
		if (!onSearchChange) return;
		const timeout = setTimeout(() => {
			onSearchChange(searchTerm);
		}, 400);
		return () => clearTimeout(timeout);
	}, [searchTerm, onSearchChange]);

	useEffect(() => {
		if (stayOpenOnUpdate && open) {
			const prevLen = prevOptionsRef.current.length;
			const newLen = options.length;
			if (prevLen !== newLen) {
				setOpen(true);
			}
		}
		prevOptionsRef.current = options;
	}, [options, stayOpenOnUpdate, open]);

	useEffect(() => {
		if (disabled) {
			setOpen(false);
		}
	}, [disabled]);

	const renderPlaceholder = () => {
		if (selected.length === 0) return placeholder;
		const selectedOptions = options.filter(o => selected.includes(o.code));
		const selectedLabels = selectedOptions.map(o => o[previewKey]);

		if (!single && options.length > 0 && selectedOptions.length === options.length) {
			if (allSelectedText) return allSelectedText;
			if (options.length > 1) return `All Selected (${options.length})`;
		}

		if (single) return selectedLabels[0];
		if (selectedLabels.length === 1) return selectedLabels[0];

		const rest = selectedLabels.slice(1).join(', ');
		return (
			<>
				{selectedLabels[0]}{' '}
				<span
					data-bs-toggle="tooltip"
					data-bs-placement="top"
					title={rest}
					style={{ cursor: 'pointer' }}
				>
					+{selectedLabels.length - 1}
				</span>
			</>
		);
	};

	useEffect(() => {
		if (!open) return;
		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as Node;
			if (
				dropdownRef.current && !dropdownRef.current.contains(target) &&
				menuRef.current && !menuRef.current.contains(target)
			) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [open]);

	useEffect(() => {
		if (open) {
			const handleScrollOrResize = () => {
				updatePosition();
			};
			window.addEventListener('scroll', handleScrollOrResize, true);
			window.addEventListener('resize', handleScrollOrResize);
			return () => {
				window.removeEventListener('scroll', handleScrollOrResize, true);
				window.removeEventListener('resize', handleScrollOrResize);
			};
		}
	}, [open, updatePosition]);

	return (
		<div
			className="position-relative w-100"
			ref={dropdownRef}
			style={{
				position: 'relative',
				...style
			}}
		>
			<button
				ref={buttonRef}
				style={{
					border: '1px solid #dee2e6',
					background: 'white',
					position: 'relative',
					zIndex: 1,
					...buttonStyle,
				}}
				className={btnClassName || "btn dropdown-toggle d-flex justify-content-between align-items-center w-100 py-2"}
				type="button"
				onClick={(e) => {
					if (disabled) return;
					e.preventDefault();
					e.stopPropagation();
					setOpen(!open);
				}}
				disabled={disabled}
			>
				<span className="me-2 text-start w-100 text-truncate" style={{ color: 'black' }}>
					{renderPlaceholder()}
				</span>
			</button>

			{open && createPortal(
				<div
					ref={menuRef}
					className="dropdown-menu show p-0 animate-pop-in"
					style={{
						position: 'fixed',
						top: coords.dropUp ? 'auto' : `${coords.top}px`,
						bottom: coords.dropUp ? `${coords.bottom}px` : 'auto',
						left: `${coords.left}px`,
						minWidth: `${coords.width}px`,
						maxWidth: '90vw',
						zIndex: 99999,
						border: '2px solid #dee2e6',
						backgroundColor: 'white',
						boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
						display: 'flex',
						flexDirection: 'column',
						maxHeight: `${coords.maxHeight}px`,
						...dropdownStyle
					}}
				>
					<div className="p-1">
						<input
							type="text"
							className="form-control form-control-sm p-1"
							style={{ fontSize: '0.75rem' }}
							placeholder="Search..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							onClick={(e) => e.stopPropagation()}
							autoFocus
						/>
					</div>

					<div
						className="d-flex flex-column gap-0"
						style={{
							flex: 1,
							overflowY: 'auto',
						}}
					>
						{showSelectAll && !single && options.length > 0 && !searchTerm && (
							<button
								type="button"
								className={isAllSelected
									? 'btn btn-primary text-start text-white py-2 px-2'
									: 'btn btn-outline-primary text-start py-2 px-2'}
								style={{
									borderRadius: 0,
									border: 0,
									borderBottom: '2px solid rgba(0,0,0,0.12)',
									whiteSpace: 'normal',
									wordBreak: 'break-word',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									height: 'auto',
									minHeight: 'fit-content',
									padding: '8px 12px',
								}}
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									if (isAllSelected) {
										clearAll();
									} else {
										selectAll();
									}
								}}
							>
								<div style={{ fontWeight: 700, fontSize: '0.75rem', lineHeight: '1.25' }}>
									<i className={`fa-solid ${isAllSelected ? 'fa-check-double' : 'fa-list-check'} me-2`}></i>
									Select All
								</div>
								<span
									className={`badge ${isAllSelected ? 'bg-white text-primary' : 'bg-primary text-white'}`}
									style={{ fontSize: '0.68rem', fontWeight: 600 }}
								>
									{selected.length > 0 ? `${selected.length}/${options.length}` : `${options.length}`}
								</span>
							</button>
						)}

						{filtered.length === 0 ? (
							<div className="p-2 text-center text-muted small">
								No data found
							</div>
						) : (
							filtered.map((opt) => {
								const isSelected = selected.includes(opt.code);
								const btnClass = isSelected
									? 'btn btn-primary text-start text-white py-2 px-2'
									: 'btn btn-outline-primary text-start py-2 px-2';

								return (
									<button
										key={opt.code}
										type="button"
										className={btnClass}
										style={{
											borderRadius: 0,
											border: 0,
											borderBottom: '1px solid rgba(0,0,0,0.06)',
											whiteSpace: 'normal',
											wordBreak: 'break-word',
											display: 'block',
											height: 'auto',
											minHeight: 'fit-content',
											padding: '8px 12px',
										}}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											toggleOption(opt.code);
										}}
									>
										<div style={{ fontWeight: 600, fontSize: '0.75rem', lineHeight: '1.25', whiteSpace: 'normal' }}>
											{opt[previewKey]}
										</div>
										{subKey && opt[subKey] && (
											<div style={{ fontSize: '0.68rem', opacity: 0.85, marginTop: '3px', lineHeight: '1.2', whiteSpace: 'normal' }}>
												{opt[subKey]}
											</div>
										)}
									</button>
								);
							})
						)}
					</div>

					{!single && (
						<div className="p-2 border-top bg-light d-flex gap-2">
							{showSelectAll && (
								<button
									type="button"
									className="btn btn-sm btn-outline-primary flex-fill fw-semibold"
									style={{ fontSize: '0.75rem' }}
									disabled={options.length === 0}
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										if (searchTerm.trim() !== '') {
											if (isAllFilteredSelected) {
												const filteredCodes = filtered.map(opt => opt.code);
												updateSelected(selected.filter(code => !filteredCodes.includes(code)));
											} else {
												selectAll();
											}
										} else {
											if (isAllSelected) {
												clearAll();
											} else {
												selectAll();
											}
										}
									}}
								>
									{searchTerm.trim() !== ''
										? (isAllFilteredSelected ? 'Deselect Filtered' : 'Select All Filtered')
										: (isAllSelected ? 'Deselect All' : 'Select All')}
								</button>
							)}

							<button
								type="button"
								className={`btn btn-sm ${showSelectAll ? 'btn-outline-danger flex-fill' : 'btn-danger w-100'} fw-semibold`}
								style={{ fontSize: '0.75rem' }}
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									clearAll();
								}}
							>
								Clear
							</button>
						</div>
					)}
				</div>,
				document.body
			)}
		</div>
	);
};

export default MultiSelectDropdown;